import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DatePickerInput,
  Form,
  PLATFORM_THEME_COLORS,
  SwitchInput,
  Typography,
} from '@thinknet-gmbh/platform-common-ui';

import { FormInstance } from 'antd';
import { FlexRowContainer, StyledSwitchButtonContainer } from './payment-additional-fields.styles';
import { ISwitcherDefaultData } from 'src/components/employee/employee.interfaces';

const PaymentAdditionalFields = ({
  form,
  switcherDefaultData = {},
  isEmployeeEditForm,
  disabledAll,
}: {
  form: FormInstance;
  disabledAll?: boolean;
  switcherDefaultData?: ISwitcherDefaultData;
  isEmployeeEditForm?: boolean;
}) => {
  const { t, i18n } = useTranslation('employee');

  const [workPermissionNeeded, setWorkPermissionNeeded] = useState<boolean>(
    switcherDefaultData?.workPermissionNeeded || false
  );
  const [hasCertificateOfStudy, setHasCertificateOfStudy] = useState<boolean>(
    switcherDefaultData?.hasCertificateOfStudy || false
  );
  const [residencePermissionNeeded, setResidencePermissionNeeded] = useState<boolean>(
    switcherDefaultData?.residencePermissionNeeded || false
  );

  const Container = isEmployeeEditForm ? FlexRowContainer : Fragment;

  return (
    <Fragment>
      <Container>
        <StyledSwitchButtonContainer>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
            {t('paymentAndInsurance.labels.workPermissionNeeded')}:
          </Typography>
          <Form.Item
            valuePropName="checked"
            name={['paymentAndInsurance', 'workPermissionNeeded']}
            label={''}
          >
            <SwitchInput
              defaultChecked={workPermissionNeeded}
              checkedChildren={t('common:labels.yes')}
              size={'small'}
              disabled={disabledAll}
              unCheckedChildren={t('common:labels.no')}
              onChange={checked => {
                setWorkPermissionNeeded(checked);
                if (!checked) {
                  form.setFieldsValue({
                    workPermissionExpireDate: null,
                  });
                }
              }}
            />
          </Form.Item>
        </StyledSwitchButtonContainer>
        {workPermissionNeeded && (
          <Form.Item
            name={['paymentAndInsurance', 'workPermissionExpireDate']}
            label={t('paymentAndInsurance.labels.workPermissionExpireDate')}
            rules={[
              { required: workPermissionNeeded, message: t('customers:error.fieldIsRequired') },
            ]}
          >
            <DatePickerInput disabled={disabledAll} lang={i18n.language} format="DD.MM.YYYY" />
          </Form.Item>
        )}
      </Container>
      <Container>
        <StyledSwitchButtonContainer>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
            {t('paymentAndInsurance.labels.residencePermissionNeeded')}:
          </Typography>
          <Form.Item
            name={['paymentAndInsurance', 'residencePermissionNeeded']}
            valuePropName="checked"
            label={''}
          >
            <SwitchInput
              defaultChecked={residencePermissionNeeded}
              checkedChildren={t('common:labels.yes')}
              size={'small'}
              disabled={disabledAll}
              unCheckedChildren={t('common:labels.no')}
              onChange={checked => {
                setResidencePermissionNeeded(checked);
                if (!checked) {
                  form.setFieldsValue({
                    'paymentAndInsurance.residencePermissionExpireDate': null,
                  });
                }
              }}
            />
          </Form.Item>
        </StyledSwitchButtonContainer>
        {residencePermissionNeeded && (
          <Form.Item
            name={['paymentAndInsurance', 'residencePermissionExpireDate']}
            label={t('paymentAndInsurance.labels.residencePermissionExpireDate')}
            rules={[
              {
                required: residencePermissionNeeded,
                message: t('customers:error.fieldIsRequired'),
              },
            ]}
          >
            <DatePickerInput disabled={disabledAll} lang={i18n.language} format="DD.MM.YYYY" />
          </Form.Item>
        )}
      </Container>
      <Container>
        <StyledSwitchButtonContainer>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
            {t('paymentAndInsurance.labels.hasCertificateOfStudy')}
          </Typography>
          <Form.Item
            valuePropName={'checked'}
            name={['paymentAndInsurance', 'hasCertificateOfStudy']}
          >
            <SwitchInput
              defaultChecked={hasCertificateOfStudy}
              checkedChildren={t('common:labels.yes')}
              size={'small'}
              disabled={disabledAll}
              unCheckedChildren={t('common:labels.no')}
              onChange={checked => {
                setHasCertificateOfStudy(checked);
                if (!checked) {
                  form.setFieldsValue({
                    'paymentAndInsurance.certificateOfStudyExpireDate': null,
                  });
                }
              }}
            />
          </Form.Item>
        </StyledSwitchButtonContainer>
        {hasCertificateOfStudy && (
          <Form.Item
            name={['paymentAndInsurance', 'certificateOfStudyExpireDate']}
            label={t('paymentAndInsurance.labels.certificateOfStudyExpireDate')}
            rules={[
              { required: hasCertificateOfStudy, message: t('customers:error.fieldIsRequired') },
            ]}
          >
            <DatePickerInput disabled={disabledAll} lang={i18n.language} format="DD.MM.YYYY" />
          </Form.Item>
        )}
      </Container>
    </Fragment>
  );
};

export default PaymentAdditionalFields;
